//Components
import {
  Box,
  Checkbox,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Popper,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
// Helpers & Utils
import FormControlLabel from "@mui/material/FormControlLabel";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { AquariusInput } from "@smartrr/shared/customerPortal/components/AquariusInput";
import { countryFlagOptionList } from "@smartrr/shared/data/countries";
// Helpers & Utils
import { ISODateString } from "@smartrr/shared/entities/ISODateString";
import {
  IMailingAddressCreate,
  IMailingAddressWithShopifyId,
  MailingAddressType,
} from "@smartrr/shared/entities/MailingAddress";
import { useTypedForm } from "@smartrr/shared/hooks/useTypedForm";
import { ISmartrrCountry } from "@smartrr/shared/interfaces/Country";
import { CountryCode } from "@smartrr/shared/shopifyGraphQL/api";
import { getCountryByCode, getProvinceByCode } from "@smartrr/shared/utils/country";
import { localize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { CircleChevronDown } from "akar-icons";
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import * as yup from "yup";

interface IAddressFormProps {
  defaultFormValues?: IMailingAddressWithShopifyId;
  countriesList: ISmartrrCountry[];
  addressType: MailingAddressType;
  children?: ReactNode;
  isEditing?: boolean;
  showPhoneNumber?: boolean;
  currentAddress?: IMailingAddressWithShopifyId;

  onChange?: (
    mailingAddressFormValues: IMailingAddressCreate,
    isValid: boolean,
    defaultAddressChecked?: boolean
  ) => void;
}

/* Styling Start */
export const SelectWrapper = styled(Grid)`
  height: 40px;
  position: relative;

  select {
    -moz-appearance: none !important;
    -ms-appearance: none !important;
    -o-appearance: none !important;
    -webkit-appearance: none !important;
    appearance: none !important;
    background: transparent;
    background-image: none;
    border: ${props => props.theme.modernButtonBorderWidth}px solid
      ${props => props.theme.modernSecondaryButtonBorderColor};
    border-radius: ${props => props.theme.modernButtonBorderRadius}px;
    cursor: pointer;
    font-family: ${props => props.theme.modernBodyFontFamily};
    font-size: ${props => props.theme.modernBodyFontSize};
    height: 40px; /* Firefox */
    padding: 0; /* Safari and Chrome */
    padding-left: 10px;
    width: 100%;
  }
`;
const Icon = styled(CircleChevronDown)`
  position: absolute;
  right: 10px;
  top: 10px;
`;
const FormWrapper = styled("div")`
  margin-top: 15px;

  h4 {
    margin-bottom: 0px;
  }

  & .form-validation-error {
    color: #c61e00;
    font-size: 12px;
    margin-top: 5px;
  }
`;

const FlagImgContainer = styled("div")`
  height: 18px;
  width: 27px;
`;

export const AquariusFormRow = styled(Grid)`
  margin-top: 10px;

  & [class*="address-input"] {
    position: relative;

    & .form-validation-error {
      position: absolute;
      right: 10px;
      top: 5px;
    }
  }
`;

const StyledCheck = styled(FormControlLabel)`
  padding: 15px 0;
`;

/* Styling End */

export function AddressForm({
  defaultFormValues,
  countriesList,
  addressType,
  isEditing,
  onChange,
  currentAddress,
  showPhoneNumber = true,
}: IAddressFormProps): JSX.Element {
  const [popperAnchorElement, setPopperAnchorElement] = React.useState<null | HTMLElement>(null);
  const [selectedPhoneCountryIndex, setSelectedPhoneCountryIndex] = useState<number>();

  const localizeShorthand = "settings.shippingAddress";
  const isCurrentAddress = currentAddress && defaultFormValues && currentAddress.id === defaultFormValues?.id;
  const [defaultAddressChecked, setDefaultAddressChecked] = useState<boolean>(false);
  const theme = useTheme();
  const activeLanguage = theme.translationOverrides?.activeLanguage ?? "en";

  const lanuageCountryMap = {
    en: "US",
    fr: "FR",
    es: "ES",
    pt: "PT",
  };

  const isPopperOpen = Boolean(popperAnchorElement);

  const defaultCountry =
    defaultFormValues?.countryCode || activeLanguage
      ? countriesList.find(country => {
          if (defaultFormValues?.countryCode) {
            return country.countryCode === defaultFormValues?.countryCode;
          }
          return country.countryCode === lanuageCountryMap[activeLanguage as keyof typeof lanuageCountryMap];
        })
      : countriesList.find(country => country.countryCode === CountryCode.Us);

  const defaultPhoneCountryCode = defaultFormValues?.phoneCountryCode || defaultCountry?.countryCode;

  const defaultProvince =
    defaultCountry && defaultFormValues?.provinceCode
      ? defaultCountry?.provinces.find(province => province.provinceCode === defaultFormValues.provinceCode)
      : undefined;

  function createValidationSchema(countriesList: ISmartrrCountry[]) {
    return yup.object().shape({
      firstName: yup.string().required("Required"),
      lastName: yup.string().required("Required"),
      address1: yup.string().required("Required"),
      address2: yup.string(),
      city: yup.string().required("Required"),
      country: yup.mixed<CountryCode>().oneOf(Object.values(CountryCode)),
      province: yup.string().when("country", {
        is: countryCode => !!countriesList.find(c => c.countryCode === countryCode)?.provinces.length,
        then: yup.string().required("Required"),
      }),
      zip: yup.string().when("country", {
        is: countryCode => !!countriesList.find(c => c.countryCode === countryCode)?.zipRequired,
        then: yup.string().required("Required"),
      }),
      ...(addressType === "GIFTING"
        ? { email: yup.string().email().required("Required") }
        : { email: yup.string() }),
      phone: yup
        .string()
        .matches(
          /^\s*(?:\+?(\d{1,3}))?[ (.-]*(\d{3})[ ).-]*(\d{3})[ .-]*(\d{4})(?: *x(\d+))?\s*$/,
          "Please enter valid number."
        )
        .required("Required"),
      phoneCountryCode: yup.mixed<CountryCode>().oneOf(Object.values(CountryCode)),
    });
  }

  const { useField, useValues, validateForm, setFieldValue } = useTypedForm({
    validationSchema: createValidationSchema(countriesList),
    initialValues: {
      address1: defaultFormValues?.address1 || "",
      address2: defaultFormValues?.address2 || "",
      city: defaultFormValues?.city || "",
      country: (defaultCountry?.countryCode || CountryCode.Us) as CountryCode,
      firstName: defaultFormValues?.firstName || "",
      lastName: defaultFormValues?.lastName || "",
      province: defaultProvince?.provinceCode,
      zip: defaultFormValues?.zip || "",
      email: "",
      phone: defaultFormValues?.phone || "",
      phoneCountryCode: (defaultPhoneCountryCode || CountryCode.Us) as CountryCode,
    },
    onSubmit() {
      /* NOOP */
    },
  });

  const values = useValues();
  const selectedCountry = countriesList.find(country => country.countryCode === values.country);

  const [selectedPhoneCountryCode, setSelectedPhoneCountryCode] = useState<CountryCode>(
    (values?.phoneCountryCode || CountryCode.Us) as CountryCode
  );

  // Forcing component to listen for updates to isEditing (ie. switching from 'Add Address' -> 'Edit Address')
  useEffect(() => {
    setFieldValue("address1", defaultFormValues?.address1 || "");
    setFieldValue("address2", defaultFormValues?.address2 || "");
    setFieldValue("city", defaultFormValues?.city || "");
    setFieldValue("country", (defaultCountry?.countryCode || CountryCode.Us) as CountryCode);
    setFieldValue("firstName", defaultFormValues?.firstName || "");
    setFieldValue("lastName", defaultFormValues?.lastName || "");
    setFieldValue("province", defaultProvince?.provinceCode);
    setFieldValue("zip", defaultFormValues?.zip || "");
    setFieldValue("email", "");
    setFieldValue("phone", defaultFormValues?.phone || "");
  }, [isEditing, defaultFormValues]);

  useEffect(() => {
    if (selectedCountry?.provinces.length && !values.province) {
      setFieldValue("province", selectedCountry?.provinces[0].provinceCode);
    }
    if (!selectedCountry?.provinces.length) {
      setFieldValue("province", undefined);
    }
    if (!selectedCountry?.zipRequired && values.zip) {
      setFieldValue("zip", undefined);
    }
    validateForm().then(errors => {
      onChange &&
        onChange(
          {
            ...values,
            country: values.country ? getCountryByCode(values.country)?.country : undefined,
            countryCode: values.country,
            province: values.province ? getProvinceByCode(values.country, values.province)?.province : undefined,
            provinceCode: values.province,
            addressType,
            shopifyUpdateDate: ISODateString.toString(new Date()),
          },
          !Object.keys(errors).length,
          defaultAddressChecked
        );
    });
  }, [values, addressType, selectedCountry, defaultAddressChecked]);

  useEffect(() => {
    setFieldValue("phoneCountryCode", selectedPhoneCountryCode);
  }, [selectedPhoneCountryCode]);

  useEffect(() => {
    setSelectedPhoneCountryCode(values.country);
  }, [values.country]);

  useEffect(() => {
    setSelectedPhoneCountryCode((defaultPhoneCountryCode || CountryCode.Us) as CountryCode);
  }, [defaultPhoneCountryCode]);

  const { Input: FirstNameInput, ValidationError: FirstNameValidationError } = useField("firstName");
  const { Input: LastNameInput, ValidationError: LastNameValidationError } = useField("lastName");
  const { Input: Address1Input, ValidationError: Address1ValidationError } = useField("address1");
  const { Input: Address2Input } = useField("address2");
  const { Input: CityInput, ValidationError: CityValidationError } = useField("city");
  const { Dropdown: CountrySelect, ValidationError: CountryValidationError } = useField("country");
  const { Dropdown: ProvinceSelect, ValidationError: ProvinceValidationError } = useField("province");
  const { Input: PhoneInput, ValidationError: PhoneValidationError } = useField("phone");
  const { Input: EmailInput, ValidationError: EmailValidationError } = useField("email");
  const { Input: ZipInput, ValidationError: ZipValidationError } = useField("zip");

  const countriesListOptions = useMemo(
    () =>
      (countriesList || []).map(country => ({
        label: country.country,
        value: country.countryCode as CountryCode,
      })),
    [countriesList]
  );

  const provinceListOptions = useMemo(() => {
    if (selectedCountry?.provinces.length) {
      return selectedCountry.provinces.map(province => ({
        label: province.province,
        value: province.provinceCode,
      }));
    }
  }, [selectedCountry]);

  const handlePopperClick = (event: React.MouseEvent<HTMLElement>) => {
    setPopperAnchorElement(popperAnchorElement ? null : event.currentTarget);
  };

  const handleListItemClick = (event: React.MouseEvent<HTMLElement>, index: number, value: CountryCode) => {
    setSelectedPhoneCountryIndex(index);
    handlePopperClick(event);
    setSelectedPhoneCountryCode(value);
  };

  const getFlagListItems = () => {
    return countryFlagOptionList.map((option, index) => (
      <ListItemButton
        key={option.label}
        selected={selectedPhoneCountryIndex === index}
        onClick={event => {
          handleListItemClick(event, index, option.value);
        }}
      >
        <ListItemText primary={option.label} />
      </ListItemButton>
    ));
  };

  return (
    <FormWrapper role={"form"}>
      <Typography variant="h4">
        {localize(
          `${localizeShorthand}.${
            addressType === "GIFTING" ? "giftAddressHeader" : isEditing ? "editAddressHeader" : "newAddressHeader"
          }`
        )}
      </Typography>
      <AquariusFormRow container spacing={"10px"}>
        <AquariusInput item xs={6}>
          <FirstNameInput placeholder={localize("addressForm.firstName")} type="text" />
          <FirstNameValidationError hideIfFormClean useIcons />
        </AquariusInput>
        <AquariusInput item xs={6}>
          <LastNameInput placeholder={localize("addressForm.lastName")} type="text" />
          <LastNameValidationError hideIfFormClean useIcons />
        </AquariusInput>
      </AquariusFormRow>
      <AquariusFormRow container spacing={"10px"}>
        <AquariusInput item xs={12}>
          <Address1Input placeholder={localize("addressForm.addressOne")} type="text" />
          <Address1ValidationError hideIfFormClean useIcons />
        </AquariusInput>
      </AquariusFormRow>
      <AquariusFormRow container spacing={"10px"}>
        <AquariusInput item xs={12}>
          <Address2Input placeholder={localize("addressForm.addressTwo")} type="text" />
        </AquariusInput>
      </AquariusFormRow>
      <AquariusFormRow container spacing={"10px"}>
        <AquariusInput item xs={6}>
          <CityInput placeholder={localize("addressForm.city")} type="text" />
          <CityValidationError hideIfFormClean useIcons />
        </AquariusInput>
        <AquariusInput
          item
          xs={6}
          sx={{
            "& .form-validation-error": {
              right: "35px !important",
            },
          }}
        >
          <SelectWrapper>
            <CountrySelect options={countriesListOptions} aria-label={localize("addressForm.country")} />
            <Icon strokeWidth={2} size={18} color={theme.modernIconsColor} />
          </SelectWrapper>
          <CountryValidationError hideIfFormClean useIcons />
        </AquariusInput>
      </AquariusFormRow>

      <AquariusFormRow container spacing={"10px"}>
        <AquariusInput item xs={6}>
          <ZipInput
            placeholder={localize("addressForm.zip")}
            // @ts-ignore
            type="text"
          />
          <ZipValidationError hideIfFormClean useIcons />
        </AquariusInput>
        {selectedCountry?.provinces.length && provinceListOptions ? (
          <AquariusInput
            item
            xs={6}
            sx={{
              "& .form-validation-error": {
                right: "35px !important",
              },
            }}
          >
            <SelectWrapper>
              <ProvinceSelect options={provinceListOptions!} aria-label={selectedCountry?.provinceLabel} />
              <Icon strokeWidth={2} size={18} color={theme.modernIconsColor} />
            </SelectWrapper>
            <ProvinceValidationError hideIfFormClean useIcons />
          </AquariusInput>
        ) : null}
      </AquariusFormRow>

      {addressType === MailingAddressType.GIFTING ? (
        <AquariusFormRow container spacing={"10px"}>
          <AquariusInput item xs={12}>
            <EmailInput placeholder={localize("addressForm.emailAddress")} type="text" />
            <EmailValidationError hideIfFormClean useIcons />
          </AquariusInput>
        </AquariusFormRow>
      ) : null}

      {showPhoneNumber ? (
        <AquariusFormRow container spacing={"10px"}>
          <AquariusInput item xs={9.9}>
            {/* @ts-ignore */}
            <PhoneInput placeholder={localize("addressForm.phoneNumber")} type="text" />
            <PhoneValidationError hideIfFormClean useIcons />
          </AquariusInput>
          <AquariusInput item xs={2.1}>
            <AquariusButton
              disableRipple={true}
              autoFocus={false}
              onClick={handlePopperClick}
              sx={{
                marginTop: 0,
                backgroundColor: theme.modernCardBackground,
                "&:hover": {
                  background: theme.modernCardBackground,
                },
              }}
              endIcon={<Icon strokeWidth={2} size={18} color={theme.modernIconsColor} />}
            >
              <FlagImgContainer>
                <img
                  alt="Country Flag"
                  src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${
                    selectedPhoneCountryCode || values.phoneCountryCode
                  }.svg`}
                />{" "}
              </FlagImgContainer>
            </AquariusButton>
            <Popper open={isPopperOpen} anchorEl={popperAnchorElement} disablePortal={true} placement="top-end">
              <Box
                sx={{
                  borderRadius: ".5rem",
                  border: 0.5,
                  p: 1,
                  bgcolor: "white",
                  zIndex: 99999,
                  height: 350,
                  width: 350,
                }}
                overflow="scroll"
              >
                <List>{getFlagListItems()}</List>
              </Box>
            </Popper>
          </AquariusInput>
        </AquariusFormRow>
      ) : null}

      {isEditing && !isCurrentAddress ? (
        <AquariusFormRow
          sx={{
            margin: "0px !important",
            "& label": {
              marginBottom: "0px",
              paddingBottom: "0px !important",
            },
          }}
        >
          <StyledCheck
            control={
              <Checkbox
                defaultChecked
                checked={defaultAddressChecked}
                onChange={() => setDefaultAddressChecked(!defaultAddressChecked)}
              />
            }
            label={localize(`${localizeShorthand}.setDefaultCheckBox`)}
          />
        </AquariusFormRow>
      ) : null}
    </FormWrapper>
  );
}
